import React, { FC, useEffect, useState } from 'react';
import styled from 'styled-components';
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined';
import BusinessCenterOutlinedIcon from '@material-ui/icons/BusinessCenterOutlined';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import { ErrorLable } from './MultiCheckbox';

type Option = {
  id: number;
  title: string;
  hint: string;
  Icon: React.ComponentType<{ style?: React.CSSProperties }>;
};

const OPTIONS: Option[] = [
  {
    id: 1,
    title: 'Owner Builder',
    hint: "I own the property and I'm doing the work myself.",
    Icon: HomeOutlinedIcon,
  },
  {
    id: 2,
    title: 'Contractor',
    hint: "I'm a licensed contractor doing the work for a client.",
    Icon: BusinessCenterOutlinedIcon,
  },
];

type Props = {
  setValue: Function;
  trigger: Function;
  register: Function;
  errors: any;
};

const NAME = 'applicantType';

const ApplicantTypeCards: FC<Props> = ({ setValue, trigger, register, errors }) => {
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [initLoad, setInitLoad] = useState<boolean>(true);
  const hasError = errors[NAME] !== undefined;

  useEffect(() => {
    register(NAME);
  }, [register]);

  useEffect(() => {
    if (initLoad) {
      setInitLoad(false);
      return;
    }
    const value = selectedId
      ? [{ id: selectedId, title: OPTIONS.find(o => o.id === selectedId)?.title }]
      : [];
    setValue(NAME, value);
    const performTrigger = async () => {
      await trigger(NAME);
    };
    performTrigger();
  }, [selectedId]);

  return (
    <Wrapper>
      {hasError && <ErrorLable>{errors[NAME].message}</ErrorLable>}
      <CardsRow role="radiogroup" aria-label="Applicant type">
        {OPTIONS.map(opt => {
          const checked = selectedId === opt.id;
          const { Icon } = opt;
          return (
            <Card
              key={opt.id}
              type="button"
              role="radio"
              aria-checked={checked}
              onClick={() => setSelectedId(opt.id)}
              $checked={checked}
              $error={hasError && !checked}
            >
              <IconTile $checked={checked}>
                <Icon style={{ fontSize: 26 }} />
              </IconTile>
              <Body>
                <Title>{opt.title}</Title>
                <Hint>{opt.hint}</Hint>
              </Body>
              <CheckIndicator $checked={checked} aria-hidden>
                {checked && <CheckRoundedIcon style={{ fontSize: 16 }} />}
              </CheckIndicator>
            </Card>
          );
        })}
      </CardsRow>
    </Wrapper>
  );
};

export default ApplicantTypeCards;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CardsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;

  @media only screen and (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.button<{ $checked: boolean; $error: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: start;
  gap: 14px;
  text-align: left;
  padding: 16px 18px;
  background: ${({ $checked }) => ($checked ? 'var(--primary-50)' : 'var(--neutral-0)')};
  border: 1.5px solid
    ${({ $checked, $error }) => {
    if ($checked) return 'var(--primary-600)';
    if ($error) return 'var(--danger-500, #dc2626)';
    return 'var(--border)';
  }};
  border-radius: 12px;
  cursor: pointer;
  font-family: inherit;
  transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: ${({ $checked }) => ($checked ? 'var(--primary-700, #0b5a87)' : 'var(--neutral-400)')};
    background: ${({ $checked }) => ($checked ? 'var(--primary-50)' : 'var(--neutral-50)')};
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  @media only screen and (max-width: 540px) {
    padding: 14px;
    gap: 12px;
  }
`;

const IconTile = styled.span<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  flex-shrink: 0;
  background: ${({ $checked }) => ($checked ? 'var(--primary-600)' : 'var(--primary-50)')};
  color: ${({ $checked }) => ($checked ? 'var(--neutral-0)' : 'var(--primary-600)')};
  transition: background-color 0.15s ease, color 0.15s ease;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const Title = styled.span`
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--mainText);
  line-height: 1.3;
`;

const Hint = styled.p`
  margin: 0;
  font-size: 0.8125rem;
  color: var(--neutral-600);
  line-height: 1.45;
`;

const CheckIndicator = styled.span<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  flex-shrink: 0;
  border: 1.5px solid ${({ $checked }) => ($checked ? 'var(--primary-600)' : 'var(--neutral-300)')};
  background: ${({ $checked }) => ($checked ? 'var(--primary-600)' : 'var(--neutral-0)')};
  color: var(--neutral-0);
  margin-top: 2px;
  transition: background-color 0.15s ease, border-color 0.15s ease;
`;
